import { useState, useEffect } from 'react';
import { Form, Select, DatePicker, Button, Card, InputNumber, Input, message, Spin, notification } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

import { useGetPlansQuery } from '../../services/package';
import { useGetBranchesQuery } from '../../services/branches';
import { useGetAllCouponQuery } from '../../services/coupons';
import { useGetEmployeeByCustomerQuery } from '../../services/employee';
import { useGetInvoiceByIdQuery, useUpdateInvoiceMutation } from '../../services/invoice';
import { useGetOpenTrainersQuery } from '../../services/trainer';
import { AllInvoiceRoute } from '../../routes/routepath';
import ImagePicker from '../../components/form/ImagePicker';
import usePermissions from '../../hooks/usePermissions';
import './styles.scss';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface PaymentMode {
  id:     number;
  mode:   string;
  amount: string | number;
}

/* ─── Constants ──────────────────────────────────────────────────────── */

const GST_PERCENTAGE = 5;

const PAYMENT_MODE_OPTIONS = [
  { value: 'cash',         label: 'Cash'          },
  { value: 'card',         label: 'Card'          },
  { value: 'upi',          label: 'UPI'           },
  { value: 'banktransfer', label: 'Bank Transfer' },
  { value: 'cheque',       label: 'Cheque'        },
  { value: 'creditnote',   label: 'Credit Note'   },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'fullPayment', label: 'Full Payment'    },
  { value: 'partial',     label: 'Partial Payment' },
];

const GST_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no',  label: 'No'  },
];

/* Bare "YYYY-MM-DD" strings (no time/offset) get parsed by the JS Date spec as
   UTC midnight, so displaying them with showTime shows a bogus IST-shifted
   time instead of a real one. Parse full timestamps as-is (real, correct
   local time) and bare dates explicitly as local calendar dates (midnight
   local, not a shifted fake time). */
const parseDateTime = (value?: string | null) => {
  if (!value) return null;
  return value.includes('T') ? dayjs(value) : dayjs(value, 'YYYY-MM-DD');
};

/* ─── Component ──────────────────────────────────────────────────────── */

const EditInvoice = () => {
  const navigate = useNavigate();
  const { id }   = useParams<{ id: string }>();
  const [form]   = Form.useForm();

  const [userData,        setUserData]        = useState<any | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<any | null>(null);
  /* editable plan price — starts at the invoice's charged amount but can be
     overridden by hand, independent of the (locked) plan's own current pricing. */
  const [planPrice,       setPlanPrice]        = useState(0);
  const [loading,         setLoading]         = useState(false);
  const [gstClaim,        setGstClaim]        = useState(false);
  const [selectedCoupon,  setSelectedCoupon]  = useState<any | null>(null);
  const [discountAmount,  setDiscountAmount]  = useState(0);
  const [paymentModes,    setPaymentModes]    = useState<PaymentMode[]>([{ id: 1, mode: '', amount: '' }]);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [remainingAmount, setRemainingAmount] = useState(0);
  /* number of payment entries that already existed on the invoice — these stay
     read-only; only entries added via "Add" (index >= this count) are editable
     and get sent to the backend as new paymentTerm entries. */
  const [originalPaymentCount, setOriginalPaymentCount] = useState(0);

  const { data: invoiceData,  isLoading: invoiceLoading,   error: invoiceError   } =
    useGetInvoiceByIdQuery(id as any, { refetchOnMountOrArgChange: true });
  const { data: packagesData, isLoading: plansLoading     } = useGetPlansQuery({ limit: 1000 } as any);
  const { data: branchesData, isLoading: _branchesLoading } = useGetBranchesQuery(undefined);
  const { data: couponsData,  isLoading: couponsLoading   } = useGetAllCouponQuery({ limit: 1000 } as any);
  const { data: employeesData, isLoading: employeesLoading } = useGetEmployeeByCustomerQuery(
    (userData as any)?._id as any,
    { skip: !(userData as any)?._id }
  );
  const [updateInvoice, { isLoading: updatingInvoice }] = useUpdateInvoiceMutation();

  /* Invoice Date is editable only with INVOICE_DATE_ACCESS (same rule as Purchase Membership Plan / Purchase Add-On Service). */
  const { hasPermission } = usePermissions();
  const canBackdateInvoice = hasPermission('INVOICE_DATE_ACCESS');
  /* Payment Date is editable only with INVOICE_PAYMENT_DATE_EDIT. */
  const canEditPaymentDate = hasPermission('INVOICE_PAYMENT_DATE_EDIT');

  const invoice: any = (invoiceData as any)?.data;
  const isAddonInvoice = invoice?.membershipId?.type === 'addon';
  const invoiceBranchId: string | undefined = invoice?.branchId?._id;

  const { data: trainersData, isLoading: trainersLoading } = useGetOpenTrainersQuery(
    { page: 1, limit: 100, branchId: invoiceBranchId },
    { skip: !isAddonInvoice || !invoiceBranchId }
  );

  const packages: any[] = (packagesData as any)?.data || [];
  const coupons:  any[] = (couponsData  as any)?.data || [];

  /* addon invoice ke liye trainer options — sirf matching specialization wale */
  const addonType: string | undefined = invoice?.serviceType || invoice?.planId?.addonType;
  const addonTypeNormalized = addonType
    ? addonType.replace(/[-_]/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
    : null;

  const allTrainers: any[] = (trainersData as any)?.data ?? [];
  const filteredTrainers = addonTypeNormalized
    ? allTrainers.filter((t: any) =>
        t.specialization?.some((s: string) => s.toLowerCase() === addonTypeNormalized.toLowerCase()))
    : allTrainers;

  const trainerOptions = filteredTrainers.length > 0
    ? filteredTrainers.map((t: any) => ({
        value: t._id,
        label: `${t.user?.name || t.name || 'Unknown'} - ${t.specialization?.join(', ') || 'No Specialization'}`,
      }))
    : [{ label: 'No trainers available for this service', value: '', disabled: true }];

  /* invoice ka plan agar plans list me na ho (inactive/deleted/paginated out)
     to bhi Select me uska naam dikhe, ObjectId nahi */
  const currentInvoicePlan: any = (invoiceData as any)?.data?.planId;
  const planOptions = [
    ...(currentInvoicePlan?._id && !packages.some((p: any) => p._id === currentInvoicePlan._id)
      ? [{ value: currentInvoicePlan._id, label: `${currentInvoicePlan.name} - ₹${currentInvoicePlan.pricing}` }]
      : []),
    ...packages.map((pkg: any) => ({ value: pkg._id, label: `${pkg.name} - ₹${pkg.pricing}` })),
  ];

  /* ── Helpers ── */

  const calculateTotals = (basePrice: number, coupon: any, _isGstClaim: boolean, _gstPercent: number) => {
    let discountAmt = 0;
    if (coupon) {
      discountAmt = coupon.discountType === 'percentage'
        ? (basePrice * coupon.value) / 100
        : coupon.value;
    }
    const priceAfterDiscount = Math.max(0, basePrice - discountAmt);
    setDiscountAmount(discountAmt);
    form.setFieldsValue({ afterDiscount: priceAfterDiscount, totalOrderValue: priceAfterDiscount });
  };

  const calculatePaymentAmounts = () => {
    const formValues = form.getFieldsValue();
    let total = 0;
    paymentModes.forEach(payment => {
      total += Number(formValues[`paymentAmount_${payment.id}`] || 0);
    });
    setTotalPaidAmount(total);
    if (selectedPackage) {
      setRemainingAmount((planPrice - discountAmount) - total);
    }
  };

  /* ── Handlers ── */

  const handlePackageChange = (packageId: string) => {
    const pkg = packages.find((p: any) => p._id === packageId);
    setSelectedPackage(pkg);
    if (pkg) {
      setPlanPrice(pkg.pricing ?? 0);
      calculateTotals(pkg.pricing ?? 0, selectedCoupon, gstClaim, GST_PERCENTAGE);
      form.setFieldsValue({ planPrice: pkg.pricing ?? 0 });
    }
  };


  const handlePaymentModeChange = (mode: string, paymentId: number) => {
    setPaymentModes(prev => prev.map(p => p.id === paymentId ? { ...p, mode } : p));
    if (mode === 'cash') {
      try {
        const loggedInUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (loggedInUser.name) form.setFieldsValue({ [`cashEmployee_${paymentId}`]: loggedInUser.name });
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
    }
  };

  const handlePaymentAmountChange = (amount: number | null, paymentId: number) => {
    setPaymentModes(prev => prev.map(p => p.id === paymentId ? { ...p, amount: amount ?? '' } : p));
    setTimeout(() => calculatePaymentAmounts(), 100);
  };

  const addPaymentMode = () => {
    const newId = Math.max(...paymentModes.map(p => p.id)) + 1;
    setPaymentModes(prev => [...prev, { id: newId, mode: '', amount: '' }]);
  };

  const removePaymentMode = (paymentId: number) => {
    if (paymentModes.length > 1) {
      setPaymentModes(prev => prev.filter(p => p.id !== paymentId));
      setTimeout(() => calculatePaymentAmounts(), 100);
    }
  };

  const handleStartDateChange = (date: any) => {
    if (date && selectedPackage) {
      const endDate = dayjs(date).add((selectedPackage.numberOfDays || 30) - 1, 'days');
      form.setFieldsValue({ endDate });
    }
  };

  const handleCouponChange = (couponId: string) => {
    const coupon = coupons.find((c: any) => c._id === couponId);
    setSelectedCoupon(coupon);
    calculateTotals(planPrice, coupon, gstClaim, GST_PERCENTAGE);
  };

  const handleGstClaimChange = (value: string) => {
    const isGstClaim = value === 'yes';
    setGstClaim(isGstClaim);
    calculateTotals(planPrice, selectedCoupon, isGstClaim, GST_PERCENTAGE);
  };

  /* ── Effects ── */

  useEffect(() => {
    calculatePaymentAmounts();
  }, [selectedPackage, planPrice, discountAmount, gstClaim, paymentModes.length]);

  useEffect(() => {
    const invoice: any = (invoiceData as any)?.data;
    if (!invoice) return;

    if (invoice.userId)   setUserData(invoice.userId);
    if (invoice.planId)   setSelectedPackage(invoice.planId);
    setPlanPrice(invoice.planPrice ?? invoice.planId?.pricing ?? 0);
    if (invoice.gstClaim !== undefined) setGstClaim(invoice.gstClaim);
    setSelectedCoupon(invoice.couponId || null);
    setDiscountAmount(invoice.discountAmount || 0);

    if (invoice.paymentDetails?.length > 0) {
      const loadedPayments: PaymentMode[] = invoice.paymentDetails.map((p: any, i: number) => ({
        id:     i + 1,
        mode:   p.modeOfPayment,
        amount: p.amount,
      }));
      setPaymentModes(loadedPayments);
      setOriginalPaymentCount(loadedPayments.length);

      const paymentFieldValues: Record<string, any> = {};
      invoice.paymentDetails.forEach((p: any, i: number) => {
        const pid = i + 1;
        paymentFieldValues[`paymentMode_${pid}`]   = p.modeOfPayment;
        paymentFieldValues[`paymentAmount_${pid}`] = p.amount;
        paymentFieldValues[`paymentDate_${pid}`]   = p.paymentDate ? dayjs(p.paymentDate) : null;
        if (p.modeOfPayment === 'cash'   && p.receivedBy)               paymentFieldValues[`cashEmployee_${pid}`]    = p.receivedBy;
        if (p.modeOfPayment === 'upi'    && p.receipt?.length)          paymentFieldValues[`upiScreenshot_${pid}`]   = p.receipt[0];
        if (p.modeOfPayment === 'card'   && p.receipt?.length)          paymentFieldValues[`cardScreenshot_${pid}`]  = p.receipt[0];
        if (p.modeOfPayment === 'cheque') {
          paymentFieldValues[`chequeNumber_${pid}`] = p.chequeNumber || '';
          paymentFieldValues[`chequeDate_${pid}`]   = p.chequeDate ? dayjs(p.chequeDate) : null;
        }
      });
      form.setFieldsValue(paymentFieldValues);
    }

    // salesPersonId is the actual Sales Person (employee record _id, matching the
    // Sales Person Select's options); employeeId is a separate field used for the
    // addon-trainer flow, not the Sales Person shown/edited here.
    const salesPersonId = invoice.salesPersonId?._id || invoice.employeeId?._id;
    /* addon invoice ka trainer us membership se lo jo is invoice ka addon hai */
    const addonMembership =
      invoice.userMemberships?.find((m: any) => m._id === invoice.membershipId?._id) ||
      invoice.userMemberships?.find((m: any) => m.type === 'addon') ||
      invoice.userMemberships?.[0];
    /* the top-level invoiceDate comes back date-only ("2026-08-24", no time) —
       the full timestamp (with the real time the invoice was created) only
       survives on the embedded membershipInvoices copy of this same invoice. */
    const preciseInvoice =
      invoice.membershipInvoices?.find((m: any) => m._id === invoice._id) ||
      invoice.membershipInvoices?.[0];
    form.setFieldsValue({
      userId:                 invoice.userId?._id,
      branchId:               invoice.branchId?._id,
      planId:                 invoice.planId?._id,
      planPrice:              invoice.planPrice,
      startDate:              invoice.startDate    ? dayjs(invoice.startDate.split('T')[0])    : null,
      endDate:                invoice.expiryDate   ? dayjs(invoice.expiryDate.split('T')[0])   : null,
      invoiceDate:            parseDateTime(preciseInvoice?.invoiceDate || invoice.invoiceDate),
      paymentDate:            invoice.paymentDate  ? dayjs(invoice.paymentDate.split('T')[0])  : null,
      couponSelect:           invoice.couponId?._id,
      discountAmount:         invoice.discountAmount,
      afterDiscount:          invoice.afterDiscount,
      gstClaim:               invoice.gstClaim ? 'yes' : 'no',
      gstNumber:              invoice.gstNumber,
      registeredCompanyName:  invoice.registeredCompanyName,
      totalOrderValue:        invoice.totalAmount,
      paymentType:            invoice.paymentType === 'fullPayment' ? 'fullPayment' : 'partial',
      salesPerson:            salesPersonId,
      lockerNumber:           invoice.userMemberships?.[0]?.lockerNumber,
      coachId:                addonMembership?.coachId,
    });
  }, [invoiceData, form]);

  /* ── onFinish ── */

  const onFinishFailed = ({ errorFields }: { errorFields: { name: (string | number)[]; errors: string[] }[] }) => {
    if (!errorFields?.length) return;
    const labels = errorFields.map(f => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return String(name).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    });
    notification.error({
      message:     'Required Fields Missing',
      description: [...new Set(labels)].join(', '),
      placement:   'topRight',
      duration:    4,
    });
  };

  const onFinish = async (values: Record<string, any>) => {
    setLoading(true);
    try {
      const startDate = values.startDate
        ? dayjs(values.startDate).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
      const endDate = values.endDate
        ? dayjs(values.endDate).format('YYYY-MM-DD')
        : dayjs(startDate).add(selectedPackage?.numberOfDays || 30, 'days').format('YYYY-MM-DD');

      const payload: Record<string, any> = {
        id,
        customerName:   userData?.name,
        customerEmail:  userData?.email,
        customerNumber: userData?.phoneNumber,

        planId:  values.planId,
        planPrice,
        couponId: values.couponSelect || null,

        gstClaim,
        gstNumber:             gstClaim ? (values.gstNumber || null) : null,
        registeredCompanyName: gstClaim ? (values.registeredCompanyName || null) : null,

        dueDate: invoice?.dueDate ? dayjs(invoice.dueDate).format('YYYY-MM-DD') : null,
        paymentType: values.paymentType,
        status:      invoice?.status,

        startDate,
        expiryDate: endDate,
      };

      if (isAddonInvoice) {
        if (values.coachId) payload.employeeId = values.coachId;
      } else if (values.salesPerson) {
        payload.salesPersonId = values.salesPerson;
      }

      // invoiceDate/paymentDate are always the invoice's real stored values
      // (only their edit-ability is permission-gated), so always safe to resend.
      if (values.invoiceDate) {
        payload.invoiceDate = dayjs(values.invoiceDate).format('YYYY-MM-DD HH:mm:ss');
      }
      if (values.paymentDate) {
        payload.paymentDate = dayjs(values.paymentDate).format('YYYY-MM-DD');
      }

      // Amount edits on already-recorded payments (mode/other fields on these stay locked).
      const editedPaymentDetails: Record<string, any>[] = [];
      paymentModes.slice(0, originalPaymentCount).forEach((payment, i) => {
        const amount   = form.getFieldValue(`paymentAmount_${payment.id}`) || 0;
        const original = invoice?.paymentDetails?.[i];
        if (original && amount !== original.amount) {
          editedPaymentDetails.push({ _id: original._id, amount });
        }
      });
      if (editedPaymentDetails.length) payload.paymentDetails = editedPaymentDetails;

      // Newly added payment modes (existing/original ones are read-only and already persisted).
      const todayStr = dayjs().format('YYYY-MM-DD');
      const newPaymentTerms: Record<string, any>[] = [];
      paymentModes.slice(originalPaymentCount).forEach(payment => {
        const modeOfPayment = form.getFieldValue(`paymentMode_${payment.id}`) as string;
        const amount        = form.getFieldValue(`paymentAmount_${payment.id}`) || 0;
        if (!modeOfPayment || !amount) return;

        switch (modeOfPayment) {
          case 'cash':
            newPaymentTerms.push({ modeOfPayment: 'cash', amount, paymentDate: todayStr, receivedBy: form.getFieldValue(`cashEmployee_${payment.id}`) || null, receipt: [] });
            break;
          case 'upi': {
            const upiScreenshot  = form.getFieldValue(`upiScreenshot_${payment.id}`);
            const upiReferenceId = form.getFieldValue(`upiReferenceId_${payment.id}`);
            newPaymentTerms.push({ modeOfPayment: 'upi', amount, paymentDate: todayStr, receipt: upiScreenshot ? [upiScreenshot] : [], referenceId: upiReferenceId || null });
            break;
          }
          case 'card': {
            const cardScreenshot = form.getFieldValue(`cardScreenshot_${payment.id}`);
            newPaymentTerms.push({ modeOfPayment: 'card', amount, paymentDate: todayStr, receipt: cardScreenshot ? [cardScreenshot] : [] });
            break;
          }
          case 'cheque': {
            const chequeNumber     = form.getFieldValue(`chequeNumber_${payment.id}`);
            const chequeScreenshot = form.getFieldValue(`chequeScreenshot_${payment.id}`);
            newPaymentTerms.push({ modeOfPayment: 'cheque', amount, paymentDate: todayStr, chequeNumber: chequeNumber || null, receipt: chequeScreenshot ? [chequeScreenshot] : [] });
            break;
          }
          case 'banktransfer': {
            const transferScreenshot = form.getFieldValue(`transferScreenshot_${payment.id}`);
            newPaymentTerms.push({ modeOfPayment: 'banktransfer', amount, paymentDate: todayStr, receipt: transferScreenshot ? [transferScreenshot] : [] });
            break;
          }
          case 'creditnote': {
            const creditNoteScreenshot = form.getFieldValue(`creditNoteUpload_${payment.id}`);
            newPaymentTerms.push({ modeOfPayment: 'creditnote', amount, paymentDate: todayStr, receipt: creditNoteScreenshot ? [creditNoteScreenshot] : [] });
            break;
          }
          default: break;
        }
      });
      if (newPaymentTerms.length) payload.paymentTerm = newPaymentTerms;

      await (updateInvoice as any)(payload).unwrap();
      message.success('Invoice updated successfully!');
      navigate(AllInvoiceRoute);
    } catch (error) {
      message.error('Failed to update invoice. Please try again.');
      console.error('Update invoice error:', error);
    } finally {
      setLoading(false);
    }
  };

  /* ── Early returns ── */

  if (invoiceLoading) {
    return (
      <div className="buy-plan-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Loading invoice..." />
      </div>
    );
  }

  if (invoiceError) {
    return (
      <div className="buy-plan-container" style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: 'red', fontSize: '16px' }}>Failed to load invoice. Please try again.</p>
      </div>
    );
  }

  /* ── Render ── */

  return (
    <div className="buy-plan-container">
      <div className="form-header">
        <h2>Edit Invoice</h2>
      </div>

      {userData && (
        <Card style={{ marginBottom: '24px', background: 'var(--hover-bg)', border: '1px solid var(--muted)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div><strong>Customer Name:</strong><div>{(userData as any).name}</div></div>
            <div><strong>Email:</strong><div>{(userData as any).email}</div></div>
            <div><strong>Phone Number:</strong><div>{(userData as any).phoneNumber}</div></div>
            <div><strong>Status:</strong><div style={{ textTransform: 'capitalize' }}>{(userData as any).status}</div></div>
          </div>
        </Card>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed as any}
        className="custom-form"
      >
        <div className="row">
          <Form.Item name="invoiceDate" label="Invoice Date" initialValue={dayjs()}>
            <DatePicker
              picker="date"
              style={{ width: '100%', height: '46px' }}
              placeholder="Invoice date & time"
              format="DD-MM-YYYY hh:mm A"
              showTime={{ format: 'hh:mm A', use12Hours: true }}
              disabled={!canBackdateInvoice}
              disabledDate={canBackdateInvoice ? undefined : current => current && !current.isSame(dayjs(), 'day')}
            />
          </Form.Item>
          <Form.Item name="paymentDate" label="Payment Date">
            <DatePicker
              style={{ width: '100%', height: '46px' }}
              placeholder="Select payment date"
              format="DD-MM-YYYY"
              disabled={!canEditPaymentDate}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
          </Form.Item>
          {isAddonInvoice ?
<Form.Item name="coachId" label="Select Trainer">
            <Select
              placeholder="Choose trainer"
              loading={trainersLoading}
              showSearch
              disabled
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={trainerOptions as any}
            />
          </Form.Item>:
          <Form.Item name="salesPerson" label="Sales Person">
            <Select
              placeholder="Choose sales person"
              loading={employeesLoading}
              showSearch
              disabled
              options={(() => {
                const opts = ((employeesData as any)?.data || []).map((emp: any) => ({
                  value: emp._id,
                  label: `${emp?.name || 'Unknown'} - ${emp?.employeeId || emp?.roleId?.name || 'No ID'}`,
                }));
                // The invoice's own sales person may not be in this customer's
                // employee list (e.g. a different branch) — fall back to the
                // name already embedded on the invoice so it never shows a raw id.
                const currentId = invoice?.salesPersonId?._id || invoice?.employeeId?._id;
                if (currentId && !opts.some((o: any) => o.value === currentId)) {
                  const currentName = invoice?.salesPersonId?.name || invoice?.employeeId?.user?.name || invoice?.employeeId?.name;
                  if (currentName) opts.unshift({ value: currentId, label: currentName });
                }
                return opts;
              })()}
            />
          </Form.Item>
}
        </div>

        <div className="row">
          <Form.Item name="planId" label="Plan Select">
            <Select
              placeholder="Choose plan"
              loading={plansLoading}
              onChange={handlePackageChange}
              showSearch
              disabled
              options={planOptions}
            />
          </Form.Item>
          <Form.Item name="paymentType" label="Payment Type">
            <Select placeholder="Select payment type" options={PAYMENT_TYPE_OPTIONS} />
          </Form.Item>
        </div>

        <div className="row">
          <Form.Item name="planPrice" label="Plan Price">
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => (value ?? '').replace(/₹\s?|(,*)/g, '') as any}
              disabled
              placeholder="Auto-filled from plan"
            />
          </Form.Item>
          <Form.Item name="couponSelect" label="Coupon Select">
            <Select
              placeholder="Choose coupon (optional)"
              loading={couponsLoading}
              onChange={handleCouponChange}
              allowClear
              showSearch
              options={coupons.map((c: any) => ({
                value: c._id,
                label: `${c.code} - ${c.discountType === 'percentage' ? `${c.value}%` : `₹${c.value}`} off`,
              }))}
            />
          </Form.Item>
        </div>

        {selectedPackage && (
          <div className="row">
            <Form.Item name="afterDiscount" label="After Discount">
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => (value ?? '').replace(/₹\s?|(,*)/g, '') as any}
                disabled
                placeholder="Price after coupon discount"
              />
            </Form.Item>
            <Form.Item name="gstClaim" label="GST Claim">
              <Select placeholder="Select GST claim" onChange={handleGstClaimChange} options={GST_OPTIONS} />
            </Form.Item>
          </div>
        )}

        {gstClaim && (
          <div className="row">
            <Form.Item name="gstNumber" label="GST Number">
              <Input placeholder="Enter GST number" />
            </Form.Item>
            <Form.Item name="registeredCompanyName" label="Registered Company Name">
              <Input placeholder="Enter registered company name" />
            </Form.Item>
          </div>
        )}

        {/* Payment Modes */}
        <div className="payment-modes-section">
          <h3 style={{ marginBottom: '16px', color: 'var(--sider-text)' }}>Payment Details</h3>
          {paymentModes.map((payment, index) => {
            const currentMode: string = form.getFieldValue(`paymentMode_${payment.id}`);
            const isNew = index >= originalPaymentCount;
            return (
              <div key={payment.id} className="payment-group">
                <div className="row payment-row">
                  <Form.Item
                    name={`paymentMode_${payment.id}`}
                    label={index === 0 ? 'Mode of Payment' : 'Additional Payment Mode'}
                    rules={isNew ? [{ required: true, message: 'Please select payment mode' }] : []}
                  >
                    <Select
                      placeholder="Select payment mode"
                      onChange={(value: string) => handlePaymentModeChange(value, payment.id)}
                      disabled={!isNew}
                      options={PAYMENT_MODE_OPTIONS}
                    />
                  </Form.Item>
                  <Form.Item
                    name={`paymentAmount_${payment.id}`}
                    label="Amount"
                    rules={[{ required: true, message: 'Please enter amount' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value) => (value ?? '').replace(/₹\s?|(,*)/g, '') as any}
                      placeholder="Enter amount"
                      min={0}
                      onChange={(value) => handlePaymentAmountChange(value, payment.id)}
                    />
                  </Form.Item>
                  <div className="payment-actions">
                    {index === paymentModes.length - 1 && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={addPaymentMode} style={{ marginRight: '8px' }}>Add</Button>
                    )}
                    {isNew && (
                      <Button danger icon={<DeleteOutlined />} onClick={() => removePaymentMode(payment.id)}>Remove</Button>
                    )}
                  </div>
                </div>

                {currentMode === 'cash' && (
                  <div className="row payment-details">
                    <Form.Item name={`cashEmployee_${payment.id}`} label="Employee (Cash Handler)">
                      <Input placeholder="Employee handling cash payment" style={{ width: '100%' }} disabled={!isNew} />
                    </Form.Item>
                  </div>
                )}
                {currentMode === 'upi' && (
                  <div className="row payment-details">
                    <Form.Item name={`upiScreenshot_${payment.id}`} label="UPI Payment Screenshot">
                      <ImagePicker form={form} name={`upiScreenshot_${payment.id}`} />
                    </Form.Item>
                    <Form.Item name={`upiReferenceId_${payment.id}`} label="UPI Reference ID">
                      <Input placeholder="Enter UPI transaction reference ID (optional)" style={{ width: '100%' }} disabled={!isNew} />
                    </Form.Item>
                  </div>
                )}
                {currentMode === 'cheque' && (
                  <div className="row payment-details">
                    <Form.Item name={`chequeNumber_${payment.id}`} label="Cheque Number">
                      <Input placeholder="Enter cheque number" style={{ width: '100%' }} disabled={!isNew} />
                    </Form.Item>
                    <Form.Item name={`chequeScreenshot_${payment.id}`} label="Upload Cheque Image">
                      <ImagePicker form={form} name={`chequeScreenshot_${payment.id}`} />
                    </Form.Item>
                  </div>
                )}
                {currentMode === 'card' && (
                  <div className="row payment-details">
                    <Form.Item name={`cardScreenshot_${payment.id}`} label="Card Payment Screenshot">
                      <ImagePicker form={form} name={`cardScreenshot_${payment.id}`} />
                    </Form.Item>
                  </div>
                )}
                {currentMode === 'banktransfer' && (
                  <div className="row payment-details">
                    <Form.Item name={`transferScreenshot_${payment.id}`} label="Upload Transfer Screenshot">
                      <ImagePicker form={form} name={`transferScreenshot_${payment.id}`} />
                    </Form.Item>
                  </div>
                )}
                {currentMode === 'creditnote' && (
                  <div className="row payment-details">
                    <Form.Item name={`creditNoteUpload_${payment.id}`} label="Credit Note">
                      <ImagePicker form={form} name={`creditNoteUpload_${payment.id}`} />
                    </Form.Item>
                  </div>
                )}
              </div>
            );
          })}

          {selectedPackage && (
            <div className="remaining-balance">
              {remainingAmount > 0 ? (
                <span className="balance-text remaining">
                  <strong>Remaining Balance: ₹{(remainingAmount ?? 0).toFixed(2)}</strong>
                </span>
              ) : remainingAmount < 0 ? (
                <span className="balance-text" style={{ color: '#ff4d4f' }}>
                  <strong>Exceeded Amount: ₹{Math.abs(remainingAmount ?? 0).toFixed(2)}</strong>
                </span>
              ) : (
                <span className="balance-text complete">
                  <strong>Remaining Balance: ₹{(remainingAmount ?? 0).toFixed(2)}</strong>
                </span>
              )}
            </div>
          )}
        </div>

        {selectedPackage && (
          <Card className="package-summary" size="small" title="Order Summary">
            <div className="summary-row">
              <span><strong>Package:</strong> {selectedPackage.name}</span>
              <span><strong>Plan Price:</strong> ₹{planPrice ?? 0}</span>
            </div>
            {selectedCoupon && (
              <div className="summary-row">
                <span><strong>Coupon:</strong> {selectedCoupon.code}</span>
                <span><strong>Discount:</strong> -₹{(discountAmount ?? 0).toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row">
              <span><strong>Price After Discount:</strong></span>
              <span>₹{selectedCoupon ? ((planPrice ?? 0) - (discountAmount ?? 0)).toFixed(2) : (planPrice ?? 0)}</span>
            </div>
            {totalPaidAmount > 0 && (
              <>
                <div className="summary-row">
                  <span><strong>Base Amount (without GST):</strong></span>
                  <span>₹{((totalPaidAmount ?? 0) / 1.05).toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span><strong>SGST (2.5%):</strong></span>
                  <span>₹{(((totalPaidAmount ?? 0) / 1.05) * 0.025).toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span><strong>CGST (2.5%):</strong></span>
                  <span>₹{(((totalPaidAmount ?? 0) / 1.05) * 0.025).toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span><strong>Total Paid (with GST):</strong></span>
                  <span>₹{(totalPaidAmount ?? 0).toFixed(2)}</span>
                </div>
              </>
            )}
            {selectedPackage.description && (
              <div className="summary-row full-width">
                <span><strong>Description:</strong> {selectedPackage.description}</span>
              </div>
            )}
          </Card>
        )}

        {selectedPackage && (
          <div className="date-selection-section">
            <div className="row">
              <Form.Item name="startDate" label="Start Date">
                <DatePicker style={{ width: '100%', height: '46px' }} placeholder="Select start date" format="DD-MM-YYYY" onChange={handleStartDateChange} />
              </Form.Item>
              <Form.Item name="endDate" label="End Date">
                <DatePicker style={{ width: '100%', height: '46px' }} placeholder="Auto-calculated end date" format="DD-MM-YYYY" disabled />
              </Form.Item>
            </div>
          </div>
        )}

        <div className="footer-buttons">
          <Button
            type="primary"
            htmlType="submit"
            loading={loading || updatingInvoice}
            className="save-btn"
            disabled={!selectedPackage}
          >
            Update Invoice
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditInvoice;
